// @ts-check
/** @typedef {import('../types').Device} Device */
import React, { useState, useMemo } from 'react';
import { Table, Pagination, Input, InputGroup } from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';

import { MdQrCode2 as IconQRCode, MdOutlineEdit as IconEdit } from 'react-icons/md';

import { Button as RsuiteButton, IconButton, Whisper, Tooltip } from 'rsuite';
import { rColours } from '../constants';

import {
  // getControllerIconText ,
  getControllerIconText_FROM_STRING,
  GetDeviceBadgeFromStatus,
  GetBadgeForversion,
} from './DisplayItems';
import { GiHammerBreak as IconHammer } from 'react-icons/gi';
import { getLimitOptions } from '../utils/pagination';
import { HEADER_STYLE, ELLIPSIS_STYLE, stripedRowClassName } from './tableStyles';
import { ColumnsMenu } from './ColumnsMenu';
import { useColumnWidths } from '../hooks/useColumnWidths';
import './dataTable.css';

const { Column, HeaderCell, Cell } = Table;

// Columns whose text a global filter scans (case-insensitive substring),
// reproducing the old Cloudscape TextFilter "search any column" behaviour.
const SEARCH_FIELDS = [
  'uniqueDeviceId',
  'modelType',
  'userName',
  'usersDeviceName',
  'serialNumber',
  'versionNumber',
  'dateMade',
  'connStatDesc',
];

// Toggleable columns for the "Columns" show/hide picker (ID is always shown).
// Replaces the Cloudscape CollectionPreferences content-display panel.
const COLUMN_META = [
  { id: 'modelType', label: 'Model' },
  { id: 'name', label: 'Name' },
  { id: 'user_name', label: 'User Name' },
  { id: 'serial_number', label: 'Serial' },
  { id: 'status', label: 'Status' },
  { id: 'dateMade', label: 'Date Made' },
  { id: 'version', label: 'Version' },
  { id: 'qr', label: 'Actions' },
];
const DEFAULT_VISIBLE_COLUMNS = COLUMN_META.map((c) => c.id);
const COLUMN_PICKER_DATA = COLUMN_META.map((c) => ({ label: c.label, value: c.id }));

// Default column widths (px). Columns are fixed-width + resizable so every
// header separator is draggable like Cloudscape's (see useColumnWidths for why
// resize needs a fixed width). The Name column is the exception: it stays
// flexGrow so it absorbs any leftover width (keeping the table full-width with
// no right-edge gap) and gets the most room for long names; it ellipsizes when
// squeezed but isn't itself drag-resizable.
const DEFAULT_COLUMN_WIDTHS = {
  uniqueDeviceId: 190,
  modelType: 170,
  usersDeviceName: 170,
  serialNumber: 130,
  status: 120,
  dateMade: 150,
  versionNumber: 120,
  qr: 110,
};

/** @param {string | undefined} s */
function parseDDMMYYYY(s) {
  if (!s || typeof s !== 'string') return 0;
  const parts = s.split('/');
  if (parts.length !== 3) return 0;
  const [d, m, y] = parts.map(Number);
  if (!d || !m || !y) return 0;
  return new Date(y, m - 1, d).valueOf();
}

/** @param {Device} device */
function deviceStatusRank(device) {
  const connOffline = device.connStatDesc !== 'ONLINE';
  // currentFaults can arrive as a number or a string (see Device typedef), so
  // exclude the "all clear" sentinel (2147483648) in both forms.
  const hasFault =
    device.currentFaults &&
    device.currentFaults !== 0 &&
    device.currentFaults !== '0' &&
    device.currentFaults !== null &&
    device.currentFaults !== 2147483648 &&
    device.currentFaults !== '2147483648';
  if (connOffline) return 0;
  if (hasFault) return 1;
  return 2;
}

/** @type {(a: Device, b: Device) => number} */
const statusComparator = (a, b) => {
  const rankDiff = deviceStatusRank(a) - deviceStatusRank(b);
  if (rankDiff !== 0) return rankDiff;
  return (a.userName || '').localeCompare(b.userName || '');
};

/** @type {(a: Device, b: Device) => number} */
const dateMadeComparator = (a, b) => {
  const dateDiff = parseDDMMYYYY(a.dateMade) - parseDDMMYYYY(b.dateMade);
  if (dateDiff !== 0) return dateDiff;
  return (a.userName || '').localeCompare(b.userName || '');
};

// Columns with a bespoke whole-row sort; everything else falls back to a
// numeric-aware compare on the column's dataKey field.
const COMPARATORS = {
  status: statusComparator,
  dateMade: dateMadeComparator,
};

function compareValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

export function CollectionHooksTable(props) {
  const source = useMemo(() => props.devices ?? [], [props.devices]);

  const [filterText, setFilterText] = useState('');
  const [sortColumn, setSortColumn] = useState();
  const [sortType, setSortType] = useState();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const { colWidth, onColumnResize } = useColumnWidths(DEFAULT_COLUMN_WIDTHS);

  const isVisible = (id) => visibleColumns.includes(id);

  const query = filterText.trim().toLowerCase();

  const filtered = useMemo(
    () =>
      query
        ? source.filter((row) =>
            SEARCH_FIELDS.some((f) =>
              String(row[f] ?? '')
                .toLowerCase()
                .includes(query),
            ),
          )
        : source,
    [source, query],
  );

  const sorted = useMemo(() => {
    if (!sortColumn) return filtered;
    const cmp = COMPARATORS[sortColumn] ?? ((a, b) => compareValues(a[sortColumn], b[sortColumn]));
    const arr = [...filtered].sort(cmp);
    return sortType === 'desc' ? arr.reverse() : arr;
  }, [filtered, sortColumn, sortType]);

  // Page-size options adapt to the row count; the *selected* limit must be one
  // of them or rsuite's picker shows the "Select" placeholder. When the current
  // limit isn't offered (e.g. 50 selected but only 12 rows), fall back to the
  // largest available option so the picker always shows a real value.
  const limitOptions = getLimitOptions(sorted.length);
  const effectiveLimit = limitOptions.includes(limit) ? limit : limitOptions[limitOptions.length - 1];

  // page can be left pointing past the end once the row count shrinks (a
  // filter narrows results, devices refresh with fewer rows, ...) — clamp it
  // the same way effectiveLimit already clamps an unavailable limit, rather
  // than rendering an empty page while rows exist earlier in the list.
  const maxPage = Math.max(1, Math.ceil(sorted.length / effectiveLimit));
  const effectivePage = Math.min(page, maxPage);

  const paged = useMemo(
    () => sorted.slice((effectivePage - 1) * effectiveLimit, effectivePage * effectiveLimit),
    [sorted, effectivePage, effectiveLimit],
  );

  const handleFilter = (value) => {
    setFilterText(value);
    setPage(1);
  };
  const handleSort = (column, type) => {
    setSortColumn(column);
    setSortType(type);
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <h4 style={{ margin: 0, flexShrink: 0 }}>
            Controllers <span style={{ fontWeight: 'normal', color: '#8e8e93' }}>({source.length})</span>
          </h4>
          <InputGroup inside style={{ width: 220, minWidth: 160, flex: '1 1 160px' }}>
            <Input
              value={filterText}
              onChange={handleFilter}
              placeholder="Filter controllers"
              aria-label="Filter controllers"
            />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
        </div>
        <ColumnsMenu data={COLUMN_PICKER_DATA} value={visibleColumns} onChange={setVisibleColumns} />
      </div>

      <Table
        className="data-table"
        data={paged}
        autoHeight
        rowHeight={46}
        headerHeight={38}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={handleSort}
        rowClassName={stripedRowClassName}
        renderEmpty={() => <div style={{ padding: 20, textAlign: 'center' }}>No controllers</div>}
      >
        {/* Default width sized to the full 17-digit UID so the ID shows in
            full without any resizing; still drag-resizable like the rest. */}
        <Column width={colWidth('uniqueDeviceId')} resizable onResize={onColumnResize} sortable>
          <HeaderCell style={HEADER_STYLE}>ID</HeaderCell>
          <Cell dataKey="uniqueDeviceId" verticalAlign="middle">
            {(item) => (
              <RsuiteButton
                appearance="link"
                style={{ padding: 0, fontWeight: 'bold' }}
                aria-label={`View ${item.userName || item.uniqueDeviceId}`}
                onClick={() => props.onViewDevice(item.uniqueDeviceId)}
              >
                {item.uniqueDeviceId}
              </RsuiteButton>
            )}
          </Cell>
        </Column>

        {isVisible('modelType') && (
          <Column width={colWidth('modelType')} resizable onResize={onColumnResize} sortable>
            <HeaderCell style={HEADER_STYLE}>Model</HeaderCell>
            <Cell dataKey="modelType" verticalAlign="middle">
              {(item) => (
                // minWidth:0 lets the text span shrink below its content width so
                // it can ellipsize (a flex item defaults to min-width:auto, which
                // would otherwise force the row wider instead of truncating).
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <Whisper trigger={['hover', 'focus']} placement="top" speaker={<Tooltip>{item.modelType}</Tooltip>}>
                    <span tabIndex={0} style={{ display: 'inline-flex', flexShrink: 0 }}>
                      <img
                        src={getControllerIconText_FROM_STRING(item.modelType).icon}
                        alt="Controller Icon"
                        style={{ width: 30, height: 30, display: 'block' }}
                      />
                    </span>
                  </Whisper>
                  <span style={ELLIPSIS_STYLE}>{item.modelType}</span>
                </div>
              )}
            </Cell>
          </Column>
        )}

        {/* Name stays flexGrow so it fills leftover width (no right-edge gap)
            and gets the most room for long names; ellipsizes when squeezed. */}
        {isVisible('name') && (
          <Column flexGrow={1} minWidth={160} sortable>
            <HeaderCell style={HEADER_STYLE}>Name</HeaderCell>
            <Cell dataKey="userName" verticalAlign="middle">
              {(item) => <span style={ELLIPSIS_STYLE}>{item.userName}</span>}
            </Cell>
          </Column>
        )}

        {isVisible('user_name') && (
          <Column width={colWidth('usersDeviceName')} resizable onResize={onColumnResize} sortable>
            <HeaderCell style={HEADER_STYLE}>User Name</HeaderCell>
            <Cell dataKey="usersDeviceName" verticalAlign="middle">
              {(item) => <span style={ELLIPSIS_STYLE}>{item.usersDeviceName}</span>}
            </Cell>
          </Column>
        )}

        {isVisible('serial_number') && (
          <Column width={colWidth('serialNumber')} resizable onResize={onColumnResize} sortable>
            <HeaderCell style={HEADER_STYLE}>Serial</HeaderCell>
            <Cell dataKey="serialNumber" verticalAlign="middle">
              {(item) => <span style={ELLIPSIS_STYLE}>{'SN:' + item.serialNumber}</span>}
            </Cell>
          </Column>
        )}

        {isVisible('status') && (
          <Column width={colWidth('status')} resizable onResize={onColumnResize} sortable>
            <HeaderCell style={HEADER_STYLE}>Status</HeaderCell>
            <Cell dataKey="status" verticalAlign="middle">
              {(item) => GetDeviceBadgeFromStatus(item)}
            </Cell>
          </Column>
        )}

        {isVisible('dateMade') && (
          <Column width={colWidth('dateMade')} resizable onResize={onColumnResize} sortable>
            <HeaderCell style={HEADER_STYLE}>Date Made</HeaderCell>
            <Cell dataKey="dateMade" verticalAlign="middle">
              {(item) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <IconHammer color={rColours.default} style={{ flexShrink: 0 }} />
                  <span style={ELLIPSIS_STYLE}>{item.dateMade}</span>
                </div>
              )}
            </Cell>
          </Column>
        )}

        {isVisible('version') && (
          <Column width={colWidth('versionNumber')} resizable onResize={onColumnResize} sortable>
            <HeaderCell style={HEADER_STYLE}>Version</HeaderCell>
            <Cell dataKey="versionNumber" verticalAlign="middle">
              {(item) => GetBadgeForversion(item)}
            </Cell>
          </Column>
        )}

        {/* Last column: fixed (its right edge is the table edge, so no
            separator to drag there). */}
        {isVisible('qr') && (
          <Column width={colWidth('qr')}>
            <HeaderCell style={HEADER_STYLE}>Actions</HeaderCell>
            <Cell dataKey="qr" verticalAlign="middle" style={{ padding: '7px 8px' }}>
              {(item) => (
                <div style={{ display: 'flex', gap: 8 }}>
                  <IconButton
                    icon={<IconQRCode />}
                    appearance="primary"
                    color={rColours.default}
                    size="sm"
                    aria-label={`Show QR code for ${item.userName || item.uniqueDeviceId}`}
                    onClick={() => props.onQRCodeClick(item)}
                  />
                  <IconButton
                    icon={<IconEdit />}
                    appearance="primary"
                    color={rColours.default}
                    size="sm"
                    aria-label={`Edit ${item.userName || item.uniqueDeviceId}`}
                    onClick={() => props.onEditDevice(item)}
                  />
                </div>
              )}
            </Cell>
          </Column>
        )}
      </Table>

      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={7}
          size="sm"
          layout={['limit', '|', 'pager', 'skip']}
          total={sorted.length}
          limit={effectiveLimit}
          limitOptions={limitOptions}
          activePage={effectivePage}
          onChangePage={setPage}
          onChangeLimit={(l) => {
            setLimit(l);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
}
